import {ReactNode, useCallback, useState} from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {view, TextArea, css} from '@sail/ui/deprecated';
import {useTheme} from '@stripe-internal/conversations-ui';
import type {RatingValue} from './types';
import {useClientAnalytics} from '../../../Providers/ClientProvider/useClientAnalytics';
import {RatingButton} from './RatingButton';
import {CloseButton} from '../CloseButton';
import {BrandedPrimaryButton} from '../BrandedPrimaryButton';

const messages = defineMessages({
  title: {
    id: 'support_conversations_client.csat.title',
    description: 'Title of CSAT survey form',
    defaultMessage: 'How was the help you received?',
  },
  commentLabel: {
    id: 'support_conversations_client.csat.comment.label',
    description: 'Label for text area in CSAT form',
    defaultMessage: 'Any additional feedback?',
  },
  submitButton: {
    id: 'support_conversations_client.csat.submitButton.text',
    description: 'Submit button in CSAT form',
    defaultMessage: 'Submit feedback',
  },
});

type Props = {
  loading?: boolean;
  error?: ReactNode;
  analyticsParams?: Record<string, unknown>;
  onSubmit: ({rating, comment}: {rating: RatingValue; comment: string}) => void;
  onDismiss: () => void;
};

const allRatings: RatingValue[] = ['1', '2', '3', '4', '5'];

const COMMENT_MAX_LENGTH = 2000;

export const SurveyForm = ({
  loading,
  error,
  onSubmit,
  onDismiss,
  analyticsParams = {},
}: Props) => {
  const intl = useIntl();
  const analytics = useClientAnalytics();
  const theme = useTheme();

  const [rating, setRating] = useState<RatingValue | null>(null);
  const [comment, setComment] = useState<string>('');

  const canSubmit = rating && !loading;

  const handleRatingSelected = useCallback(
    (selectedRating: RatingValue) => {
      analytics.action('support_conversations_csat_survey_rating', {
        ...analyticsParams,
        rating: selectedRating,
      });
      setRating(selectedRating);
    },
    [analytics, analyticsParams],
  );

  const handleSubmit = useCallback(() => {
    if (rating) {
      onSubmit({rating, comment});
    }
  }, [rating, comment, onSubmit]);

  return (
    <view.form css={{position: css.value('relative'), maxHeight: 'fill'}}>
      <view.div
        css={{
          maxHeight: css.value('calc(100vh - 72px)'),
          marginBottom: 'medium',
          overflowY: css.value('auto'),
        }}
      >
        <view.div
          css={{
            stack: 'y',
            gap: 'large',
            minHeight: css.value('334px'),
          }}
        >
          <view.h1
            css={{
              marginBottom: 0,
              flexGrow: css.value(0),
              fontSize: css.value('24px'),
            }}
          >
            <FormattedMessage {...messages.title} />
          </view.h1>

          <view.div
            css={{
              stack: 'x',
              gap: 'large',
              margin: 'medium',
              marginTop: 'xsmall',
            }}
          >
            {allRatings.map((ratingOption) => (
              <RatingButton
                rating={ratingOption}
                dataTestId={`survey-form-rating-${ratingOption}`}
                selected={ratingOption === rating}
                disabled={loading}
                key={ratingOption}
                onClick={() => handleRatingSelected(ratingOption)}
              />
            ))}
          </view.div>

          <view.div css={{paddingLeft: 'xxsmall', paddingRight: 'xxsmall'}}>
            <TextArea
              css={{
                alignY: 'bottom',
                '& textarea': {
                  height: css.value('88px'),
                },
                '& textarea:focus': {
                  outline: theme.focusVisibleOutline,
                  focusRing: theme.focusVisibleOutline
                    ? css.value('none')
                    : undefined,
                },
              }}
              resizeable={false}
              readOnly={loading}
              label={intl.formatMessage(messages.commentLabel)}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={COMMENT_MAX_LENGTH}
              data-testid="survey-form-comment"
            />
          </view.div>

          {error ? (
            <view.p css={{font: 'body', color: 'critical'}}>{error}</view.p>
          ) : null}
        </view.div>
      </view.div>

      <BrandedPrimaryButton
        onPress={handleSubmit}
        disabled={!canSubmit}
        pending={loading}
        dataTestId="survey-form-submit"
      >
        <FormattedMessage {...messages.submitButton} />
      </BrandedPrimaryButton>

      <CloseButton
        onClick={() => onDismiss()}
        dataTestId="survey-form-close-button"
      />
    </view.form>
  );
};
